import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROLE_LABEL, type Role } from '@doit/shared';
import { ApiError, http } from '../../api/client';
import { useAuth, type CurrentUser } from '../../api/auth';
import { Alert, Button, Field, FullPageLoader } from '../../components/ui';

type InvitePreview = {
  email: string;
  fullName: string;
  role: Role;
  expiresAt: string;
};

/** პაროლის მოთხოვნები — API-ს წესების სარკე */
const RULES: { test: (v: string) => boolean; label: string }[] = [
  { test: (v) => v.length >= 8, label: 'მინიმუმ 8 სიმბოლო' },
  { test: (v) => /[a-z]/.test(v), label: 'პატარა ლათინური ასო' },
  { test: (v) => /[A-Z]/.test(v), label: 'დიდი ლათინური ასო' },
  { test: (v) => /[0-9]/.test(v), label: 'ციფრი' },
];

export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    http
      .get<InvitePreview>(`/auth/invite/${token}`)
      .then((data) => alive && setPreview(data))
      .catch((err: unknown) => {
        if (!alive) return;
        setLoadError(err instanceof ApiError ? err.message : 'ბმულის შემოწმება ვერ მოხერხდა');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('პაროლები არ ემთხვევა');
      return;
    }
    if (!RULES.every((r) => r.test(password))) {
      setError('პაროლი არ აკმაყოფილებს მოთხოვნებს');
      return;
    }

    setBusy(true);
    try {
      const res = await http.post<{ accessToken: string; user: CurrentUser }>(
        '/auth/accept-invite',
        { token, password },
      );
      setSession(res.accessToken, res.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'რეგისტრაცია ვერ დასრულდა');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Shell>
        <Alert kind="error">{loadError}</Alert>
        <p className="mt-4 text-center text-[13px] text-ink-mute">
          მიმართე ადმინისტრატორს ახალი ბმულისთვის.
        </p>
        <Button variant="secondary" className="mt-4 w-full" onClick={() => navigate('/login')}>
          შესვლის გვერდზე დაბრუნება
        </Button>
      </Shell>
    );
  }

  if (!preview) return <FullPageLoader label="ბმული მოწმდება…" />;

  return (
    <Shell>
      <div className="mb-5 rounded-lg bg-brand-50 px-4 py-3">
        <p className="text-[13px] font-medium text-brand-800">{preview.fullName}</p>
        <p className="text-[12px] text-brand-700">{preview.email}</p>
        <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-brand-600">
          {ROLE_LABEL[preview.role]}
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}

        <Field
          label="ახალი პაროლი"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <ul className="space-y-1">
          {RULES.map((rule) => {
            const ok = rule.test(password);
            return (
              <li
                key={rule.label}
                className={`flex items-center gap-2 text-[12px] ${ok ? 'text-ok' : 'text-ink-mute'}`}
              >
                <span
                  className={`inline-block size-1.5 rounded-full ${ok ? 'bg-ok' : 'bg-line-strong'}`}
                />
                {rule.label}
              </li>
            );
          })}
        </ul>

        <Field
          label="გაიმეორე პაროლი"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm && confirm !== password ? 'პაროლები არ ემთხვევა' : undefined}
        />

        <Button type="submit" loading={busy} className="w-full">
          რეგისტრაციის დასრულება
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <img src="/step-logo.png" alt="STEP IT Academy" className="size-16 object-contain" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-ink">რეგისტრაცია</h1>
            <p className="text-[13px] text-ink-mute">დააყენე პაროლი სისტემაში შესასვლელად</p>
          </div>
        </div>
        <div className="rounded-[10px] border border-line bg-surface p-6 shadow-xs">{children}</div>
      </div>
    </div>
  );
}
